/**
 * Logo Crop Editor - Editor con maschera per ritaglio logo
 * Utilizza react-easy-crop per zoom, pan e crop
 * Supporta formato circolare o rettangolare
 */
import React, { useState, useCallback } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './dialog';
import { Button } from './button';
import { Slider } from './slider';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Check, 
  X,
  Loader2,
  Upload
} from 'lucide-react';

interface LogoCropEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (croppedBlob: Blob) => Promise<void>;
  aspectRatio?: number;
  cropShape?: 'round' | 'rect';
  title?: string;
  outputSize?: number;  // Per formato quadrato/circolare
  outputWidth?: number; // Per formato rettangolare
  outputHeight?: number; // Per formato rettangolare
}

// Utility per creare l'immagine croppata
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number = 200,
  outputHeight: number = 200
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to output size
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/png',
      1
    );
  });
};

export const LogoCropEditor: React.FC<LogoCropEditorProps> = ({
  open,
  onClose,
  onSave,
  aspectRatio = 1,
  cropShape = 'round',
  title = 'Modifica Logo',
  outputSize = 200,
  outputWidth,
  outputHeight
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  // Calcola dimensioni output
  const finalWidth = outputWidth || outputSize;
  const finalHeight = outputHeight || outputSize;

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        return;
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        // Reset crop settings
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, finalWidth, finalHeight);
      await onSave(croppedBlob);
      handleClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  };

  const resetTransform = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="logo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {title}
          </DialogTitle>
          <DialogDescription>
            Carica un'immagine e posizionala all'interno della maschera
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!imageSrc ? (
            // Upload area
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="logo-crop-input"
                data-testid="logo-crop-input"
              />
              <label
                htmlFor="logo-crop-input"
                className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-muted-foreground/25 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <p className="mb-2 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Clicca per selezionare</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, SVG (Max 5MB)
                  </p>
                </div>
              </label>
            </div>
          ) : (
            // Crop editor
            <>
              {/* Crop area */}
              <div className="relative h-64 bg-muted/50 rounded-xl overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={aspectRatio}
                  cropShape={cropShape}
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      borderRadius: '0.75rem'
                    }
                  }}
                />
              </div>

              {/* Preview indicator */}
              {/* Controls */}
              <div className="space-y-4 p-4 bg-muted/30 rounded-xl">
                {/* Zoom */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <ZoomIn className="h-4 w-4" />
                      Zoom
                    </span>
                    <span className="text-sm text-muted-foreground">{Math.round(zoom * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ZoomOut className="h-4 w-4 text-muted-foreground" />
                    <Slider
                      value={[zoom]}
                      min={1}
                      max={3}
                      step={0.1}
                      onValueChange={(value) => setZoom(value[0])}
                      className="flex-1"
                      data-testid="zoom-slider"
                    />
                    <ZoomIn className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                {/* Reset button */}
                <div className="flex justify-between items-center">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={resetTransform}
                    className="text-muted-foreground"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                  
                  <label 
                    htmlFor="logo-crop-input-change"
                    className="text-sm text-primary hover:underline cursor-pointer"
                  >
                    Cambia immagine
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="logo-crop-input-change"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} data-testid="cancel-crop-btn">
            <X className="h-4 w-4 mr-2" />
            Annulla
          </Button>
          {imageSrc && (
            <Button 
              onClick={handleSave} 
              disabled={saving || !croppedAreaPixels}
              data-testid="save-crop-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Applica
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogoCropEditor;
